import { useState } from 'react'
import { Box, IconButton, Divider, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Code,
  FormatQuote,
  Undo,
  Redo,
  Image as ImageIcon,
  Link as LinkIcon,
  Fullscreen,
  FullscreenExit,
  TableChart as TableIcon,
  AddBox,
  Delete,
  ViewColumn,
  TableRows,
  MergeType,
  SplitscreenOutlined,
  BorderClear,
  BorderAll,
} from '@mui/icons-material'
import type { Editor } from '@tiptap/react'

interface TipTapToolbarProps {
  editor: Editor
  onFullscreen?: () => void
  isFullscreen?: boolean
}

export function TipTapToolbar({ editor, onFullscreen, isFullscreen }: TipTapToolbarProps) {
  const [tableMenuAnchor, setTableMenuAnchor] = useState<null | HTMLElement>(null)

  const handleImageUpload = () => {
    const url = window.prompt('Enter image URL:')
    if (url) {
      editor.chain().focus().setImage({ src: url }).run()
    }
  }

  const handleLinkAdd = () => {
    const url = window.prompt('Enter URL:')
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  const isInTable = editor.isActive('table')
  const isBorderless = isInTable && editor.getAttributes('table').borderless === true

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        p: 1,
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #ddd',
      }}
    >
      {/* Undo/Redo */}
      <Tooltip title="Undo">
        <span>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Redo">
        <span>
          <IconButton
            size="small"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Text Formatting */}
      <Tooltip title="Bold">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          color={editor.isActive('bold') ? 'primary' : 'default'}
        >
          <FormatBold fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Italic">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          color={editor.isActive('italic') ? 'primary' : 'default'}
        >
          <FormatItalic fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Underline">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          color={editor.isActive('underline') ? 'primary' : 'default'}
        >
          <FormatUnderlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Strikethrough">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          color={editor.isActive('strike') ? 'primary' : 'default'}
        >
          <StrikethroughS fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Text Alignment */}
      <Tooltip title="Align Left">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
        >
          <FormatAlignLeft fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Align Center">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
        >
          <FormatAlignCenter fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Align Right">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
        >
          <FormatAlignRight fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Lists */}
      <Tooltip title="Bullet List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          color={editor.isActive('bulletList') ? 'primary' : 'default'}
        >
          <FormatListBulleted fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Numbered List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          color={editor.isActive('orderedList') ? 'primary' : 'default'}
        >
          <FormatListNumbered fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Code & Quote */}
      <Tooltip title="Code Block">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          color={editor.isActive('codeBlock') ? 'primary' : 'default'}
        >
          <Code fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Blockquote">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          color={editor.isActive('blockquote') ? 'primary' : 'default'}
        >
          <FormatQuote fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Table */}
      <Tooltip title="Table">
        <IconButton
          size="small"
          onClick={(e) => setTableMenuAnchor(e.currentTarget)}
          color={isInTable ? 'primary' : 'default'}
        >
          <TableIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={tableMenuAnchor}
        open={Boolean(tableMenuAnchor)}
        onClose={() => setTableMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        {!isInTable ? (
          <MenuItem onClick={() => {
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run()
            setTableMenuAnchor(null)
          }}>
            <ListItemIcon><AddBox fontSize="small" /></ListItemIcon>
            <ListItemText>Insert Table</ListItemText>
          </MenuItem>
        ) : [
          <MenuItem key="add-col-after" onClick={() => {
            editor.chain().focus().addColumnAfter().run()
            setTableMenuAnchor(null)
          }}>
            <ListItemIcon><ViewColumn fontSize="small" /></ListItemIcon>
            <ListItemText>Add Column After</ListItemText>
          </MenuItem>,
          <MenuItem key="add-col-before" onClick={() => {
            editor.chain().focus().addColumnBefore().run()
            setTableMenuAnchor(null)
          }}>
            <ListItemIcon><ViewColumn fontSize="small" /></ListItemIcon>
            <ListItemText>Add Column Before</ListItemText>
          </MenuItem>,
          <MenuItem key="del-col" onClick={() => {
            editor.chain().focus().deleteColumn().run()
            setTableMenuAnchor(null)
          }}>
            <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
            <ListItemText>Delete Column</ListItemText>
          </MenuItem>,
          <Divider key="div1" />,
          <MenuItem key="add-row-after" onClick={() => {
            editor.chain().focus().addRowAfter().run()
            setTableMenuAnchor(null)
          }}>
            <ListItemIcon><TableRows fontSize="small" /></ListItemIcon>
            <ListItemText>Add Row After</ListItemText>
          </MenuItem>,
          <MenuItem key="add-row-before" onClick={() => {
            editor.chain().focus().addRowBefore().run()
            setTableMenuAnchor(null)
          }}>
            <ListItemIcon><TableRows fontSize="small" /></ListItemIcon>
            <ListItemText>Add Row Before</ListItemText>
          </MenuItem>,
          <MenuItem key="del-row" onClick={() => {
            editor.chain().focus().deleteRow().run()
            setTableMenuAnchor(null)
          }}>
            <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
            <ListItemText>Delete Row</ListItemText>
          </MenuItem>,
          <Divider key="div2" />,
          <MenuItem key="merge" onClick={() => {
            editor.chain().focus().mergeCells().run()
            setTableMenuAnchor(null)
          }} disabled={!editor.can().mergeCells()}>
            <ListItemIcon><MergeType fontSize="small" /></ListItemIcon>
            <ListItemText>Merge Cells</ListItemText>
          </MenuItem>,
          <MenuItem key="split" onClick={() => {
            editor.chain().focus().splitCell().run()
            setTableMenuAnchor(null)
          }} disabled={!editor.can().splitCell()}>
            <ListItemIcon><SplitscreenOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Split Cell</ListItemText>
          </MenuItem>,
          <Divider key="div3" />,
          <MenuItem key="toggle-borders" onClick={() => {
            editor.chain().focus().updateAttributes('table', { borderless: !isBorderless }).run()
            setTableMenuAnchor(null)
          }}>
            <ListItemIcon>{isBorderless ? <BorderAll fontSize="small" /> : <BorderClear fontSize="small" />}</ListItemIcon>
            <ListItemText>{isBorderless ? 'Show Borders' : 'Hide Borders'}</ListItemText>
          </MenuItem>,
          <Divider key="div4" />,
          <MenuItem key="del-table" onClick={() => {
            editor.chain().focus().deleteTable().run()
            setTableMenuAnchor(null)
          }}>
            <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Delete Table</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Image & Link */}
      <Tooltip title="Insert Image">
        <IconButton size="small" onClick={handleImageUpload}>
          <ImageIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Insert Link">
        <IconButton size="small" onClick={handleLinkAdd}>
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Fullscreen toggle (optional) */}
      {onFullscreen && (
        <>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={onFullscreen}>
              {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  )
}
